import * as echarts from "echarts/core";
import { BarChart, GaugeChart, PieChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  GraphicComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { LabelLayout } from "echarts/features";

echarts.use([
  BarChart,
  GaugeChart,
  PieChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  GraphicComponent,
  CanvasRenderer,
  LabelLayout,
]);

const PINK_TOP = "rgba(219, 39, 119, 0.88)";
const PINK_BASE = "rgba(219, 39, 119, 0.30)";
const PINK_DIST_TOP = "rgba(219, 39, 119, 0.70)";
const PINK_DIST_BASE = "rgba(219, 39, 119, 0.35)";
const SLATE_TEXT = "#475569";
const SLATE_DARK = "#0f172a";
const SLATE_MUTED = "#64748b";
const GRID_LINE = "#e2e8f0";
const GREEN = "#22c55e";
const AMBER = "#f59e0b";
const SLATE_BAR_TOP = "rgba(71, 85, 105, 0.78)";
const SLATE_BAR_BASE = "rgba(71, 85, 105, 0.30)";
const SKY_BAR_TOP = "rgba(14, 165, 233, 0.88)";
const SKY_BAR_BASE = "rgba(14, 165, 233, 0.32)";
const EMERALD_BAR_TOP = "rgba(16, 185, 129, 0.88)";
const EMERALD_BAR_BASE = "rgba(16, 185, 129, 0.32)";

const PREFERS_REDUCED_MOTION = (() => {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
})();

function debounce(fn, ms) {
  let t;
  return function debounced(...args) {
    clearTimeout(t);
    t = window.setTimeout(() => fn.apply(this, args), ms);
  };
}

function formatDec(x, dec) {
  const n = Number(x);
  if (Number.isNaN(n)) {
    return "0";
  }
  return n.toFixed(dec);
}

function computeOverloadStats(threshold, credits, activeFaculty) {
  const thr = Math.max(0, Math.round(Number(threshold) || 0));
  let nOver = 0;
  let maxOver = 0;
  for (let i = 0; i < credits.length; i += 1) {
    const c = credits[i];
    if (c > thr) {
      nOver += 1;
      maxOver = Math.max(maxOver, c - thr);
    }
  }
  const pct = activeFaculty > 0 ? (100 * nOver) / activeFaculty : 0;
  return { thr, nOver, pct, maxOver };
}

function pinkVerticalGradient(top, base) {
  return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: top },
    { offset: 1, color: base },
  ]);
}

function pinkHorizontalGradient(left, right) {
  return new echarts.graphic.LinearGradient(0, 0, 1, 0, [
    { offset: 0, color: left },
    { offset: 1, color: right },
  ]);
}

function makeChart(el) {
  if (!el) {
    return null;
  }
  const chart = echarts.init(el, null, { renderer: "canvas" });
  return chart;
}

function initHistogram(root, data) {
  const el = root.querySelector('[data-sas-ar-chart="histogram"]');
  if (!el) {
    return null;
  }
  const histogram = Array.isArray(data.creditHistogram) ? data.creditHistogram : [];
  if (histogram.length === 0) {
    return null;
  }
  const labels = histogram.map(
    (b) => (data.binLabels && data.binLabels[b.bin_id]) || b.bin_id
  );
  const counts = histogram.map((b) => Number(b.count) || 0);
  const activeFaculty = Number(data.activeFaculty) || 0;
  const strings = data.strings || {};
  const tooltipTmpl = strings.tooltipHist || "";
  const yAxisName = strings.histYAxis || "";

  const chart = makeChart(el);
  if (!chart) {
    return null;
  }
  chart.setOption({
    animation: !PREFERS_REDUCED_MOTION,
    grid: { left: 48, right: 16, top: 24, bottom: 56, containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter(params) {
        if (!Array.isArray(params) || params.length === 0) {
          return "";
        }
        const p = params[0];
        const c = Number(p.value) || 0;
        const pct =
          activeFaculty > 0 ? ((100 * c) / activeFaculty).toFixed(1) : "0";
        const body = tooltipTmpl
          .replace("{{count}}", String(c))
          .replace("{{pct}}", pct);
        return `<strong>${p.axisValueLabel}</strong><br/>${body}`;
      },
    },
    xAxis: {
      type: "category",
      data: labels,
      axisLine: { lineStyle: { color: GRID_LINE } },
      axisTick: { show: false },
      axisLabel: {
        color: SLATE_TEXT,
        rotate: 35,
        fontSize: 11,
        interval: 0,
      },
    },
    yAxis: {
      type: "value",
      name: yAxisName,
      nameTextStyle: { color: SLATE_TEXT, fontSize: 11 },
      minInterval: 1,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: SLATE_TEXT, fontSize: 11 },
      splitLine: { lineStyle: { color: GRID_LINE } },
    },
    series: [
      {
        type: "bar",
        data: counts,
        barMaxWidth: 48,
        itemStyle: {
          color: pinkVerticalGradient(PINK_TOP, PINK_BASE),
          borderRadius: [4, 4, 0, 0],
        },
        label: {
          show: true,
          position: "top",
          formatter: "{c}",
          color: SLATE_TEXT,
          fontSize: 11,
        },
      },
    ],
  });
  return chart;
}

function initDistribution(root, data) {
  const el = root.querySelector('[data-sas-ar-chart="distribution"]');
  if (!el) {
    return null;
  }
  const dist = data.creditLoadDistribution;
  const distMetrics = data.distributionMetrics;
  if (!dist || !Array.isArray(distMetrics) || distMetrics.length === 0) {
    return null;
  }
  const labels = distMetrics.map((m) => m.label);
  const values = distMetrics.map((m) => Number(m.value) || 0);
  const details = distMetrics.map((m) => (m && m.detail ? String(m.detail) : ""));
  const strings = data.strings || {};
  const tooltipTmpl = strings.tooltipDist || "";

  const chart = makeChart(el);
  if (!chart) {
    return null;
  }
  chart.setOption({
    animation: !PREFERS_REDUCED_MOTION,
    grid: { left: 12, right: 56, top: 16, bottom: 16, containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter(params) {
        if (!Array.isArray(params) || params.length === 0) {
          return "";
        }
        const p = params[0];
        const v = Number(p.value).toFixed(2);
        const body = tooltipTmpl.replace("{{value}}", v);
        const detail = details[p.dataIndex] || "";
        const head = `<strong>${p.axisValueLabel}</strong><br/>${body}`;
        return detail ? `${head}<br/><span style="color:#94a3b8">${detail}</span>` : head;
      },
    },
    xAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: SLATE_TEXT, fontSize: 11 },
      splitLine: { lineStyle: { color: GRID_LINE } },
    },
    yAxis: {
      type: "category",
      data: labels,
      axisLine: { lineStyle: { color: GRID_LINE } },
      axisTick: { show: false },
      axisLabel: { color: SLATE_TEXT, fontSize: 11 },
      splitLine: { show: false },
    },
    series: [
      {
        type: "bar",
        data: values,
        barMaxWidth: 26,
        itemStyle: {
          color: pinkHorizontalGradient(PINK_DIST_TOP, PINK_DIST_BASE),
          borderRadius: [0, 4, 4, 0],
        },
        label: {
          show: true,
          position: "right",
          formatter: (params) => `${Number(params.value).toFixed(2)} cr`,
          color: SLATE_TEXT,
          fontSize: 11,
        },
      },
    ],
  });
  return chart;
}

function initFulfillmentGauge(root, data) {
  const el = root.querySelector('[data-sas-ar-chart="fulfillment"]');
  if (!el) {
    return null;
  }
  const slotToBe = Number(data.slotToBeSum) || 0;
  if (slotToBe <= 0) {
    return null;
  }
  const strings = data.strings || {};
  const fillRateRaw = data.slotFillRatePct;
  const fillRatePct =
    fillRateRaw === null || fillRateRaw === undefined
      ? null
      : Math.max(0, Math.min(100, Number(fillRateRaw)));
  const splitFraction =
    fillRatePct === null ? 0 : Math.max(0, Math.min(1, fillRatePct / 100));

  const chart = makeChart(el);
  if (!chart) {
    return null;
  }

  const gaugeValue = fillRatePct === null ? 0 : fillRatePct;

  chart.setOption({
    animation: !PREFERS_REDUCED_MOTION,
    series: [
      {
        type: "gauge",
        startAngle: 200,
        endAngle: -20,
        min: 0,
        max: 100,
        radius: "85%",
        progress: { show: false },
        pointer: {
          length: "60%",
          width: 5,
          itemStyle: { color: SLATE_TEXT },
          show: fillRatePct !== null,
        },
        axisLine: {
          lineStyle: {
            width: 18,
            color: [
              [splitFraction, GREEN],
              [1, AMBER],
            ],
          },
        },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        anchor: { show: false },
        title: {
          show: true,
          offsetCenter: [0, "30%"],
          color: SLATE_MUTED,
          fontSize: 11,
        },
        detail: {
          valueAnimation: false,
          formatter: () =>
            fillRatePct === null
              ? strings.gaugeNotApplicable || "n/a"
              : `${fillRatePct.toFixed(1)}%`,
          color: SLATE_DARK,
          fontSize: 24,
          fontWeight: "700",
          offsetCenter: [0, "8%"],
        },
        data: [
          {
            value: gaugeValue,
            name: strings.gaugeFillRateLabel || "Fill rate",
          },
        ],
      },
    ],
  });
  return chart;
}

function initCourseMixDonut(root, data) {
  const el = root.querySelector('[data-sas-ar-chart="coursemix"]');
  if (!el) {
    return null;
  }
  const mix = Array.isArray(data.courseTypeMix) ? data.courseTypeMix : [];
  const total = Number(data.courseTypeMixTotal) || 0;
  if (total <= 0 || mix.length === 0) {
    return null;
  }
  const strings = data.strings || {};
  const filtered = mix.filter((m) => Number(m.value) > 0);
  const totalLabel = (strings.mixTotalPattern || "%s offerings").replace(
    "%s",
    String(total)
  );
  const centerSubtitle = strings.mixCenterLabel || "Total";

  const chart = makeChart(el);
  if (!chart) {
    return null;
  }
  chart.setOption({
    animation: !PREFERS_REDUCED_MOTION,
    tooltip: {
      trigger: "item",
      formatter: (p) =>
        `<strong>${p.name}</strong><br/>${p.value} (${p.percent}%)`,
    },
    legend: {
      bottom: 4,
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { color: SLATE_TEXT, fontSize: 12 },
    },
    graphic: [
      {
        type: "text",
        left: "center",
        top: "42%",
        style: {
          text: totalLabel,
          fill: SLATE_DARK,
          fontSize: 16,
          fontWeight: 700,
          textAlign: "center",
        },
      },
      {
        type: "text",
        left: "center",
        top: "52%",
        style: {
          text: centerSubtitle,
          fill: SLATE_MUTED,
          fontSize: 11,
          textAlign: "center",
        },
      },
    ],
    series: [
      {
        type: "pie",
        radius: ["40%", "70%"],
        center: ["50%", "48%"],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: "#ffffff", borderWidth: 2 },
        label: {
          show: true,
          formatter: "{b}: {d}%",
          color: SLATE_TEXT,
          fontSize: 11,
        },
        labelLine: {
          length: 8,
          length2: 8,
          lineStyle: { color: GRID_LINE },
        },
        data: filtered,
      },
    ],
  });
  return chart;
}

function initDesignationGroupedBar(root, data) {
  const el = root.querySelector('[data-sas-ar-chart="designationbar"]');
  if (!el) {
    return null;
  }
  const rows = Array.isArray(data.designationRows) ? data.designationRows : [];
  if (rows.length === 0) {
    return null;
  }
  const strings = data.strings || {};
  const labels = rows.map((r) => String(r.designation || ""));
  const credits = rows.map((r) => Number(r.sumCredits) || 0);
  const contact = rows.map((r) => Number(r.sumContact) || 0);
  const theory = rows.map((r) => Number(r.sumTheory) || 0);
  const lab = rows.map((r) => Number(r.sumLab) || 0);

  const fmt1 = (n) => Number(n).toFixed(1);
  const fmt2 = (n) => Number(n).toFixed(2);
  const tipHeadcount = strings.desigTipHeadcount || "Headcount";
  const tipMeanCr = strings.desigTipMeanCredits || "Mean credits / fac";
  const tipMeanCt = strings.desigTipMeanContact || "Mean contact h / fac";
  const tipShare = strings.desigTipShare || "Share of total credits";
  const tipMinCr = strings.desigTipMinCredits || "Min credits (faculty)";
  const tipMaxCr = strings.desigTipMaxCredits || "Max credits (faculty)";
  const labelCredits = strings.desigCredits || "Sum credits";
  const labelContact = strings.desigContact || "Sum contact h";
  const labelTheory = strings.desigTheory || "Sum theory h";
  const labelLab = strings.desigLab || "Sum lab h";

  const chart = makeChart(el);
  if (!chart) {
    return null;
  }
  chart.setOption({
    animation: !PREFERS_REDUCED_MOTION,
    grid: { left: 40, right: 12, top: 28, bottom: 88, containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: "rgba(255,255,255,0.98)",
      borderColor: GRID_LINE,
      borderWidth: 1,
      textStyle: { color: SLATE_DARK, fontSize: 12 },
      extraCssText: "box-shadow:0 4px 12px rgba(15,23,42,0.08); border-radius:8px;",
      formatter(params) {
        if (!Array.isArray(params) || params.length === 0) {
          return "";
        }
        const idx = params[0].dataIndex;
        const row = rows[idx] || {};
        const headcount = Number(row.headcount) || 0;
        const meanCr = Number(row.meanCredits) || 0;
        const meanCt = Number(row.meanContact) || 0;
        const share = Number(row.shareCredits) || 0;
        const hasCrRange =
          (Number(row.withLoad) || 0) > 0 &&
          row.minFacultyCredits != null &&
          row.maxFacultyCredits != null;
        const minCr = hasCrRange ? Number(row.minFacultyCredits) : NaN;
        const maxCr = hasCrRange ? Number(row.maxFacultyCredits) : NaN;
        const valuesHtml = params
          .map(
            (p) =>
              `<div style="display:flex;justify-content:space-between;gap:12px"><span>${p.marker}<span style="color:${SLATE_TEXT}">${p.seriesName}</span></span><strong style="color:${SLATE_DARK}">${fmt2(p.value)}</strong></div>`
          )
          .join("");
        const crRangeHtml =
          hasCrRange && !Number.isNaN(minCr) && !Number.isNaN(maxCr)
            ? `<div style="display:flex;justify-content:space-between;gap:12px"><span>${tipMinCr}</span><strong style="color:${SLATE_DARK}">${fmt1(minCr)}</strong></div>
          <div style="display:flex;justify-content:space-between;gap:12px"><span>${tipMaxCr}</span><strong style="color:${SLATE_DARK}">${fmt1(maxCr)}</strong></div>`
            : "";
        const detailsHtml = `<div style="margin-top:6px;padding-top:6px;border-top:1px dashed ${GRID_LINE};font-size:11px;color:${SLATE_TEXT};line-height:1.6">
          ${crRangeHtml}
          <div style="display:flex;justify-content:space-between;gap:12px"><span>${tipHeadcount}</span><strong style="color:${SLATE_DARK}">${headcount}</strong></div>
          <div style="display:flex;justify-content:space-between;gap:12px"><span>${tipMeanCr}</span><strong style="color:${SLATE_DARK}">${fmt2(meanCr)}</strong></div>
          <div style="display:flex;justify-content:space-between;gap:12px"><span>${tipMeanCt}</span><strong style="color:${SLATE_DARK}">${fmt2(meanCt)}</strong></div>
          <div style="display:flex;justify-content:space-between;gap:12px"><span>${tipShare}</span><strong style="color:${SLATE_DARK}">${fmt1(share)}%</strong></div>
        </div>`;
        return `<div style="font-weight:600;color:${SLATE_DARK};margin-bottom:4px">${params[0].axisValueLabel}</div>${valuesHtml}${detailsHtml}`;
      },
    },
    legend: {
      type: "scroll",
      bottom: 0,
      data: [labelCredits, labelContact, labelTheory, labelLab],
      textStyle: { color: SLATE_TEXT, fontSize: 11 },
      itemWidth: 12,
      itemHeight: 8,
      icon: "roundRect",
      pageButtonItemGap: 6,
    },
    xAxis: {
      type: "category",
      data: labels,
      axisLine: { lineStyle: { color: GRID_LINE } },
      axisTick: { show: false },
      axisLabel: {
        interval: 0,
        margin: 10,
        fontSize: 10,
        lineHeight: 14,
        color: SLATE_TEXT,
        formatter(value, idx) {
          const namePart =
            typeof value === "string" ? value.replace(/\s+/g, "\n") : String(value);
          const row = rows[idx];
          if (!row) {
            return namePart;
          }
          const wl = Number(row.withLoad) || 0;
          if (
            wl === 0 ||
            row.minFacultyCredits == null ||
            row.maxFacultyCredits == null
          ) {
            return namePart;
          }
          const mn = Number(row.minFacultyCredits);
          const mx = Number(row.maxFacultyCredits);
          if (Number.isNaN(mn) || Number.isNaN(mx)) {
            return namePart;
          }
          return `${namePart}\nmin ${fmt1(mn)} · max ${fmt1(mx)} cr`;
        },
      },
    },
    yAxis: {
      type: "value",
      name: strings.desigYAxis || "",
      nameTextStyle: { color: SLATE_TEXT, fontSize: 11 },
      minInterval: 1,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: SLATE_TEXT, fontSize: 11 },
      splitLine: { lineStyle: { color: GRID_LINE } },
    },
    series: [
      {
        name: labelCredits,
        type: "bar",
        data: credits,
        barMaxWidth: 16,
        itemStyle: {
          color: pinkVerticalGradient(PINK_TOP, PINK_BASE),
          borderRadius: [3, 3, 0, 0],
        },
        emphasis: { focus: "series" },
        label: {
          show: true,
          position: "top",
          formatter: (p) => (Number(p.value) > 0 ? fmt1(p.value) : ""),
          color: SLATE_DARK,
          fontSize: 9,
          fontWeight: 600,
        },
      },
      {
        name: labelContact,
        type: "bar",
        data: contact,
        barMaxWidth: 16,
        itemStyle: {
          color: pinkVerticalGradient(SLATE_BAR_TOP, SLATE_BAR_BASE),
          borderRadius: [3, 3, 0, 0],
        },
        emphasis: { focus: "series" },
        label: {
          show: true,
          position: "top",
          formatter: (p) => (Number(p.value) > 0 ? fmt1(p.value) : ""),
          color: SLATE_TEXT,
          fontSize: 9,
        },
      },
      {
        name: labelTheory,
        type: "bar",
        data: theory,
        barMaxWidth: 16,
        itemStyle: {
          color: pinkVerticalGradient(SKY_BAR_TOP, SKY_BAR_BASE),
          borderRadius: [3, 3, 0, 0],
        },
        emphasis: { focus: "series" },
        label: {
          show: true,
          position: "top",
          formatter: (p) => (Number(p.value) > 0 ? fmt1(p.value) : ""),
          color: SLATE_TEXT,
          fontSize: 9,
        },
      },
      {
        name: labelLab,
        type: "bar",
        data: lab,
        barMaxWidth: 16,
        itemStyle: {
          color: pinkVerticalGradient(EMERALD_BAR_TOP, EMERALD_BAR_BASE),
          borderRadius: [3, 3, 0, 0],
        },
        emphasis: { focus: "series" },
        label: {
          show: true,
          position: "top",
          formatter: (p) => (Number(p.value) > 0 ? fmt1(p.value) : ""),
          color: SLATE_TEXT,
          fontSize: 9,
        },
      },
    ],
  });
  return chart;
}

/**
 * Descriptive statistics dashboard charts + client-side overload threshold (report=stats only).
 */
export function initReportHubDescriptiveStats() {
  const root = document.querySelector("[data-sas-tt-ar-stats-root]");
  if (!root) {
    return;
  }

  const rawEl = document.getElementById("sas-tt-ar-stats-data");
  if (!rawEl || !rawEl.textContent) {
    return;
  }

  let data;
  try {
    data = JSON.parse(rawEl.textContent);
  } catch {
    return;
  }

  const credits = Array.isArray(data.facultyCredits) ? data.facultyCredits : [];
  const activeFaculty = data.activeFaculty || 0;
  const maxCred = data.maxFacultyCredits || 0;
  const pattern = data.overloadPattern || "";
  const facultyPattern = data.overloadFacultyPattern || "%1$s Faculty (%2$s%)";

  const charts = [
    initHistogram(root, data),
    initDistribution(root, data),
    initFulfillmentGauge(root, data),
    initCourseMixDonut(root, data),
    initDesignationGroupedBar(root, data),
  ].filter(Boolean);

  const input = root.querySelector("#overload_threshold");
  const live = root.querySelector("[data-sas-ar-overload-live]");
  const safeBand = root.querySelector("[data-sas-ar-overload-safe-band]");
  const riskBand = root.querySelector("[data-sas-ar-overload-risk-band]");
  const marker = root.querySelector("[data-sas-ar-overload-marker]");
  const bar = root.querySelector("[data-sas-ar-overload-bar]");
  const emeraldLabel = root.querySelector("[data-sas-ar-overload-emerald]");
  const roseInsideLabel = root.querySelector(
    "[data-sas-ar-overload-rose-inside]"
  );
  const roseOutsideLabel = root.querySelector(
    "[data-sas-ar-overload-rose-outside]"
  );
  const thrScaleVal = root.querySelector("[data-sas-ar-overload-scale-thr-val]");

  function formatFacultyLabel(count, pctVal) {
    return facultyPattern
      .replace("%1$s", String(count))
      .replace("%2$s", formatDec(pctVal, 1))
      .replace(/%%/g, "%");
  }

  // Decide whether the rose-inside label fits within its rose-band box; if not,
  // hide it visually (kept in DOM for stable layout) and surface the rose-outside
  // sibling label to the right of the bar.
  function updateRoseLabelPlacement(hasOverload) {
    if (!roseInsideLabel || !roseOutsideLabel) {
      return;
    }
    if (!hasOverload) {
      roseInsideLabel.style.visibility = "hidden";
      roseOutsideLabel.classList.add("hidden");
      return;
    }
    roseInsideLabel.style.visibility = "visible";
    roseOutsideLabel.classList.add("hidden");
    const overflow =
      roseInsideLabel.scrollWidth > roseInsideLabel.clientWidth + 1;
    if (overflow) {
      roseInsideLabel.style.visibility = "hidden";
      roseOutsideLabel.classList.remove("hidden");
    }
  }

  function applyOverload(threshold) {
    const { thr, nOver, pct, maxOver } = computeOverloadStats(
      threshold,
      credits,
      activeFaculty
    );
    const within = Math.max(0, activeFaculty - nOver);
    const withinPct = activeFaculty > 0 ? Math.max(0, 100 - pct) : 0;

    if (live && pattern) {
      const msg = pattern
        .replace("%1$s", String(nOver))
        .replace("%2$s", formatDec(thr, 0))
        .replace("%3$s", formatDec(pct, 1))
        .replace("%4$s", formatDec(maxOver, 2))
        .replace(/%%/g, "%");
      live.textContent = msg;
    }
    if (emeraldLabel) {
      emeraldLabel.textContent = formatFacultyLabel(within, withinPct);
    }
    const roseText = formatFacultyLabel(nOver, pct);
    if (roseInsideLabel) {
      roseInsideLabel.textContent = roseText;
    }
    if (roseOutsideLabel) {
      roseOutsideLabel.textContent = roseText;
    }
    if (thrScaleVal) {
      thrScaleVal.textContent = formatDec(thr, 1);
    }
    const bulletM = Math.max(maxCred, thr, 1);
    const thrPct = Math.min(100, Math.max(0, (100 * thr) / bulletM));
    const maxPct = Math.min(100, Math.max(0, (100 * maxCred) / bulletM));
    const riskPct = Math.max(0, maxPct - thrPct);
    if (safeBand) {
      safeBand.style.width = `${thrPct}%`;
    }
    if (riskBand) {
      riskBand.style.left = `${thrPct}%`;
      riskBand.style.width = `${riskPct}%`;
    }
    if (marker) {
      marker.style.left = `${thrPct}%`;
    }
    if (emeraldLabel) {
      emeraldLabel.style.left = "0.625rem";
      emeraldLabel.style.right = `calc(100% - ${thrPct}% + 0.5rem)`;
    }
    if (roseInsideLabel) {
      roseInsideLabel.style.left = `calc(${thrPct}% + 0.5rem)`;
      roseInsideLabel.style.right = `calc(100% - ${maxPct}% + 0.5rem)`;
    }
    updateRoseLabelPlacement(nOver > 0);
  }

  if (charts.length > 0 || bar) {
    const onResize = debounce(() => {
      for (let i = 0; i < charts.length; i += 1) {
        const c = charts[i];
        if (c && !c.isDisposed()) {
          c.resize();
        }
      }
      if (input) {
        applyOverload(input.value);
      }
    }, 150);
    window.addEventListener("resize", onResize);
  }

  function syncUrl(thr) {
    try {
      const u = new URL(window.location.href);
      u.searchParams.set("overload_threshold", formatDec(thr, 0));
      window.history.replaceState({}, "", u.toString());
    } catch {
      /* ignore */
    }
  }

  if (input) {
    applyOverload(input.value);
    let debounceTimer;
    const run = () => {
      const v = input.value;
      applyOverload(v);
      const { thr: thrRounded } = computeOverloadStats(v, credits, activeFaculty);
      syncUrl(thrRounded);
    };
    input.addEventListener("input", () => {
      clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(run, 300);
    });
    input.addEventListener("change", run);
    input.addEventListener("blur", () => {
      const { thr } = computeOverloadStats(input.value, credits, activeFaculty);
      if (String(thr) !== input.value) {
        input.value = String(thr);
      }
      applyOverload(input.value);
      syncUrl(thr);
    });
  }
}
